import { useEffect, useState } from 'react';
import { useCreatePoster, useProviders } from '../api/hooks';
import {
  POSTER_FORMATS,
  POSTER_TEMPLATES,
  RENDER_MODES,
  STYLE_PRESETS,
  type CreatePosterRequest,
  type ImageProvider,
  type Match,
  type PosterFormat,
  type PosterRenderMode,
  type PosterTemplate,
  type Tournament,
} from '../api/types';
import ErrorBanner from './ErrorBanner';
import Modal from './Modal';

function defaultRequest(tournament: Tournament, match: Match): CreatePosterRequest {
  return {
    matchId: match.id,
    provider: 'OpenAi',
    renderMode: 'FullyAiGenerated',
    format: 'Portrait45',
    template: tournament.template,
    model: null,
    stylePreset: STYLE_PRESETS[0],
    headline: '',
    subheadline: '',
    callToAction: '',
    promptOverride: '',
  };
}

/**
 * Generating belongs to the fixture: the opponent, venue, date and selected side all come from it,
 * so the poster cannot be asked for before there is something to put on it.
 */
export default function GeneratePosterDialog({
  tournament,
  match,
  open,
  onClose,
  onQueued,
}: {
  tournament: Tournament;
  match: Match;
  open: boolean;
  onClose: () => void;
  onQueued: () => void;
}) {
  const [request, setRequest] = useState<CreatePosterRequest>(() => defaultRequest(tournament, match));
  const [showAdvanced, setShowAdvanced] = useState(false);

  const providers = useProviders();
  const createPoster = useCreatePoster(tournament.id);

  const template = request.template ?? tournament.template;
  const selectedMode = RENDER_MODES.find((m) => m.value === request.renderMode);
  const usableProviders = (providers.data ?? []).filter((p) => p.available);
  const modelsForProvider =
    (providers.data ?? []).find((p) => p.provider === request.provider)?.models ?? [];
  const noProviders = providers.data !== undefined && usableProviders.length === 0;

  // Opening for a different fixture must not inherit the last one's art direction.
  useEffect(() => {
    if (open) {
      setRequest(defaultRequest(tournament, match));
      createPoster.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, match.id]);

  // The default in defaultRequest() is a guess; once the list loads, settle on one that works.
  useEffect(() => {
    if (usableProviders.length === 0) return;
    if (usableProviders.some((p) => p.provider === request.provider)) return;
    setRequest((current) => ({ ...current, provider: usableProviders[0]!.provider }));
  }, [usableProviders, request.provider]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await createPoster.mutateAsync({
      ...request,
      matchId: match.id,
      headline: request.headline || null,
      subheadline: request.subheadline || null,
      callToAction: request.callToAction || null,
      promptOverride: request.promptOverride || null,
    });
    onQueued();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="modal--form"
      title="Generate a poster"
      subtitle={`${match.roundName || `Week ${match.week}`} - v ${match.opponent}`}
    >
      <form className="modal__body form" onSubmit={(e) => void submit(e)}>
        {match.players.length === 0 && template === 'Top5' && (
          <p className="banner banner--warning">
            No side has been picked for this fixture, so the poster will have no players on it. Add
            the players to the fixture first if you want them listed.
          </p>
        )}

        <div className="grid grid--2">
          <label>
            Poster layout
            <select
              value={template}
              onChange={(e) => setRequest({ ...request, template: e.target.value as PosterTemplate })}
            >
              {POSTER_TEMPLATES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Image model
            <select
              value={request.provider}
              onChange={(e) =>
                setRequest({ ...request, provider: e.target.value as ImageProvider, model: null })
              }
            >
              {(providers.data ?? []).map((provider) => (
                <option key={provider.provider} value={provider.provider} disabled={!provider.available}>
                  {provider.displayName} ({provider.modelId})
                  {provider.available ? '' : ' - no API key'}
                </option>
              ))}
            </select>
          </label>

          <label>
            Model
            <select
              value={request.model ?? modelsForProvider[0] ?? ''}
              disabled={modelsForProvider.length === 0}
              onChange={(e) => setRequest({ ...request, model: e.target.value })}
            >
              {modelsForProvider.map((m, index) => (
                <option key={m} value={m}>
                  {m}
                  {index === 0 ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label>
            Format
            <select
              value={request.format}
              onChange={(e) => setRequest({ ...request, format: e.target.value as PosterFormat })}
            >
              {POSTER_FORMATS.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label} - {format.hint}
                </option>
              ))}
            </select>
          </label>

          <label>
            Style
            <select
              value={request.stylePreset ?? ''}
              onChange={(e) => setRequest({ ...request, stylePreset: e.target.value })}
            >
              {STYLE_PRESETS.map((style) => (
                <option key={style} value={style}>
                  {style}
                </option>
              ))}
            </select>
          </label>

          <label>
            Headline (optional)
            <input
              value={request.headline ?? ''}
              placeholder={template === 'Top5' ? 'Good Luck' : `v ${match.opponent}`}
              onChange={(e) => setRequest({ ...request, headline: e.target.value })}
            />
          </label>

          <label className="span-2">
            Closing message (optional)
            <input
              value={request.callToAction ?? ''}
              placeholder="Everyone at the club wishes you the very best of luck."
              onChange={(e) => setRequest({ ...request, callToAction: e.target.value })}
            />
          </label>
        </div>

        <fieldset className="modes">
          <legend>How should the words get onto the poster?</legend>
          {RENDER_MODES.map((mode) => (
            <label key={mode.value} className="modes__option">
              <input
                type="radio"
                name="renderMode"
                value={mode.value}
                checked={request.renderMode === mode.value}
                onChange={(e) => setRequest({ ...request, renderMode: e.target.value as PosterRenderMode })}
              />
              <span>
                <strong>{mode.label}</strong>
                <small className="muted">{mode.hint}</small>
              </span>
            </label>
          ))}
        </fieldset>

        {noProviders && (
          <p className="banner banner--error" role="alert">
            <strong>No image model is available.</strong> Neither ChatGPT Image nor Nano Banana has an
            API key in this environment. Add the Key Vault secrets and restart the API.
          </p>
        )}

        {selectedMode?.value === 'FullyAiGenerated' && (
          <p className="banner banner--warning">
            The model letters the poster itself, so names and dates can come out wrong. The finished
            poster lists the wording it should carry - proofread it against the image before posting.
          </p>
        )}

        <button type="button" className="link-button" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? 'Hide' : 'Show'} advanced art direction
        </button>

        {showAdvanced && (
          <label>
            Custom prompt (replaces the generated art direction entirely)
            <textarea
              rows={4}
              value={request.promptOverride ?? ''}
              placeholder="A low sunlit photograph of a bowls green, long shadows, shallow depth of field, no text"
              onChange={(e) => setRequest({ ...request, promptOverride: e.target.value })}
            />
          </label>
        )}

        <ErrorBanner error={createPoster.error} />
        <ErrorBanner error={providers.error} />

        <div className="row row--wrap">
          <button
            type="submit"
            className="button button--primary"
            disabled={createPoster.isPending || noProviders}
          >
            {createPoster.isPending ? 'Queuing...' : 'Generate poster'}
          </button>
          <button type="button" className="button button--ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
        <p className="muted small">
          Generation takes roughly 20 to 60 seconds. The poster appears under Posters and updates
          itself.
        </p>
      </form>
    </Modal>
  );
}
